import type { ContentItem } from '@ps/web-martech-cms-types';
import type { NamedModel, Plugin, RematchBag } from '@rematch/core';
import { merge } from 'lodash';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { connect } from '@swf/core/api/connect';
import { request } from '@swf/core/api/request';
import { requestStream } from '@swf/core/api/requestStream';
import { subscribe } from '@swf/core/api/subscribe';

import type { EffectMeta, EffectParams, RootState, Store, WidgetEffectParams } from '../types';

type Connection = ReturnType<typeof connect> | undefined;

interface WidgetModel extends NamedModel {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  widget: ContentItem & { unmount: Observable<any> };
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const createEffectPlugin = ({
  connection: conn,
  httpDomain,
  headers
}: {
  connection: Connection;
  httpDomain?: string;
  headers?: Record<string, string>;
}): Plugin => {
  const actionsObservable: EffectParams['actions'] = new Subject();
  const bagEffects: Record<string, (payload: any, rootState: RootState, meta: EffectMeta) => any> =
    {};
  return {
    config: {
      redux: {
        middlewares: [
          (store) => (next) => (action) => {
            const result = next(action);
            actionsObservable.next({ ...action, state: store.getState() });
            return result;
          }
        ]
      }
    },
    onModel(namedModel: NamedModel, rematchStore) {
      const store = rematchStore as unknown as Store;
      const { name, effects, widget } = namedModel as WidgetModel;
      const { getState, dispatch } = store;

      if (!effects) return;

      if (typeof effects === 'function') {
        throw new Error(
          'Model effects must be plain objects, global dispatch is provided to each effect.'
        );
      }

      const boundRequestOptions = (connection: Connection) => {
        const rootState = getState() as RootState;
        const origin = store.select.location.getOrigin(rootState);

        return merge(
          { httpDomain: httpDomain || origin, connection, headers },
          store.select.cms.getRequestOptions(rootState)
        );
      };

      const bindRequest =
        (connection: Connection): EffectParams['request'] =>
        (options) =>
          request(merge(boundRequestOptions(connection), options));

      const bindRequestStream = (
        connection: Connection,
        unmount?: Observable<any>
      ): WidgetEffectParams<any>['requestStream'] | undefined => {
        if (!unmount) return undefined;
        return (options, initialData) =>
          requestStream(merge(boundRequestOptions(connection), options), initialData).pipe(
            takeUntil(unmount)
          );
      };

      const bindSubscribe = (
        connection: Connection,
        unmount?: Observable<any>
      ): WidgetEffectParams<any>['subscribe'] | undefined => {
        if (!unmount) return undefined;
        return <T>(...args: Parameters<typeof subscribe>) =>
          subscribe<T>(args[0], args[1] || connection).pipe(takeUntil(unmount));
      };

      Object.entries(effects).forEach(([effectName, effect]) => {
        const key = `${name}/${effectName}`;

        bagEffects[key] = (payload: any, rootState: RootState, meta = {} as EffectMeta) =>
          effect(
            payload,
            {
              store,
              actions: widget
                ? actionsObservable.pipe(takeUntil(widget.unmount))
                : actionsObservable,
              addModel: (model: NamedModel) => rematchStore.addModel(model),
              dispatch,
              request: bindRequest(meta.connection || conn),
              requestStream: bindRequestStream(meta.connection || conn, widget?.unmount),
              rootState,
              subscribe: bindSubscribe(meta.connection || conn, widget?.unmount),
              widget,
              select: store.select
            },
            meta
          );
      });
    },
    onStoreCreated(store, bag) {
      bag.effects = bagEffects as RematchBag['effects'];
    }
  };
};

export default createEffectPlugin;
